import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Compass, RefreshCw, AlertTriangle, X, Sparkles } from "lucide-react";
import { CtaButton, MonoLabel, Tag } from "../components/kit";
import { ProfileSharpen } from "../components/ProfileSharpen";
import { ProcessingLoader } from "../components/ProcessingLoader";
import api from "../lib/api";

const TYPE_TAGS = {
  paid_social: { color: "lilac", label: "Paid social" },
  paid_search: { color: "blue", label: "Paid search" },
  owned: { color: "green", label: "Owned" },
  organic: { color: "amber", label: "Organic" },
  offline: { color: "neutral", label: "Offline" },
};

const GOAL_LABELS = { sales: "Sales", leads: "Leads", awareness: "Awareness" };
const DISCLAIMER = "Industry estimate. Your number will vary";
const DONUT_COLORS = ["#1A1712", "#E0603A", "#0B67FF", "#8FB3F0", "#CDBBA6", "#9A9591", "#F0C3AC", "#54505B"];

const eur = (n) => "€" + Math.round(n).toLocaleString("en-US");
const compact = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, "")}k` : `${Math.round(n)}`);

const budgetToPos = (b) => (100 * Math.log(b / 100)) / Math.log(500);
const posToBudget = (p) => {
  const raw = 100 * Math.pow(500, p / 100);
  if (raw < 1000) return Math.round(raw / 10) * 10;
  if (raw < 10000) return Math.round(raw / 100) * 100;
  return Math.round(raw / 500) * 500;
};

const BrandAvatar = ({ name }) => {
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    let alive = true, url = null;
    api.get("/brand-kit")
      .then(({ data }) => {
        if (!alive || !data?.has_logo) return null;
        return api.get("/brand-kit/logo", { responseType: "blob" });
      })
      .then((r) => {
        if (!alive || !r) return;
        url = URL.createObjectURL(r.data);
        setLogo(url);
      })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, []);
  return (
    <span className="relative w-11 h-11 rounded-xl bg-[#181621] text-[#FAF8F3] font-extrabold text-[16px] flex items-center justify-center overflow-hidden shrink-0" data-testid="brand-avatar">
      {(name || "?").trim().charAt(0).toUpperCase()}
      {logo && <img src={logo} alt="" className="absolute inset-0 w-full h-full object-contain bg-white p-1" onError={() => setLogo(null)} />}
    </span>
  );
};

const ContextBar = ({ brain, live, month }) => {
  const subtitle = [brain.business_type, brain.country, brain.snapshot?.what_they_sell].filter(Boolean).join(" · ");
  const stats = [
    ["Budget", `${eur(brain.monthly_budget)}/mo`],
    ["Goal", GOAL_LABELS[brain.primary_goal] || brain.primary_goal],
    ["Cadence", "Monthly"],
  ];
  return (
    <div className="card-soft mt-6 px-5 sm:px-6 py-4 flex flex-wrap items-center gap-x-6 gap-y-4" data-testid="brand-context-bar">
      <div className="flex items-center gap-3.5 min-w-0 flex-1">
        <BrandAvatar name={brain.business_name} />
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <p className="font-bold text-[15.5px] text-[#181621] truncate" data-testid="context-business-name">{brain.business_name}</p>
            {live && (
              <span className="inline-flex items-center gap-1.5 bg-[#E9F2E4] text-[#3E7B3E] rounded-full px-2.5 py-0.5 text-[11px] font-semibold" data-testid="plan-live-pill">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3E7B3E]" /> Plan live
              </span>
            )}
            {month > 1 && <Tag color="lilac" data-testid="strategy-month-tag">Month {month}</Tag>}
          </div>
          <p className="text-[12.5px] text-[#9A9591] truncate mt-0.5">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center">
        {stats.map(([k, v], i) => (
          <div key={k} className={`px-5 ${i > 0 ? "border-l border-[#EAE5DB]" : ""}`}>
            <p className="text-[11px] text-[#9A9591]">{k}</p>
            <p className="text-[14px] font-bold text-[#181621] tabular-nums whitespace-nowrap">{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProjectedCard = ({ active, projection }) => (
  <div className="card-soft p-5 sm:p-6 flex flex-col" data-testid="projected-results-card">
    <div className="flex items-baseline justify-between gap-2">
      <p className="text-[13px] font-semibold text-[#54505B]">Projected results</p>
      <span className="text-[11px] text-[#9A9591]">estimate/mo</span>
    </div>
    {projection ? (
      <>
        <p className="mt-3 text-[26px] font-extrabold tracking-tight text-[#181621] tabular-nums" data-testid="projected-number">
          {compact(projection.low)}–{compact(projection.high)} <span className="text-[15px] font-semibold text-[#54505B]">{projection.metric}</span>
        </p>
        <p className="text-[12px] text-[#9A9591] mt-1">at {eur(active.monthly_budget)}/mo · across {active.channels.length} active channels</p>
        <div className="mt-4 h-1.5 rounded-full" style={{ background: "linear-gradient(90deg,#E0603A,#0B67FF)" }} />
        <div className="flex justify-between mt-1.5 text-[10.5px] text-[#9A9591] tabular-nums">
          <span>conservative {compact(projection.low)}</span>
          <span>{compact(projection.high)} strong</span>
        </div>
      </>
    ) : (
      <p className="mt-3 text-[26px] font-extrabold text-[#9A9591]">—</p>
    )}
    <div className="mt-auto pt-4 flex justify-between text-[10.5px] text-[#9A9591]">
      <span>Basis: Industry est.</span>
      <span>Horizon: This month</span>
    </div>
  </div>
);

const DonutCard = ({ active }) => {
  const [hover, setHover] = useState(null);
  const total = active.channels.reduce((s, c) => s + c.share_pct, 0) || 100;
  const R = 50, C = 2 * Math.PI * R;
  let acc = 0;
  const segs = active.channels.map((c, i) => {
    const len = (c.share_pct / total) * C;
    const seg = { c, i, len, off: acc };
    acc += len;
    return seg;
  });
  return (
    <div className="card-soft p-5 sm:p-6" data-testid="budget-donut-card">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[13px] font-semibold text-[#54505B]">Budget allocation</p>
        <span className="text-[11px] text-[#9A9591]">by channel</span>
      </div>
      <div className="mt-3 flex items-center gap-5 flex-wrap sm:flex-nowrap">
        <div className="relative w-[140px] h-[140px] shrink-0">
          <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
            {segs.map((s) => (
              <circle
                key={s.i} cx="70" cy="70" r={R} fill="none"
                stroke={DONUT_COLORS[s.i % DONUT_COLORS.length]} strokeWidth="21"
                strokeDasharray={`${Math.max(s.len - 2, 1)} ${C - Math.max(s.len - 2, 1)}`}
                strokeDashoffset={-s.off}
                style={{ opacity: hover === null || hover === s.i ? 1 : 0.3, transition: "opacity .18s" }}
                onMouseEnter={() => setHover(s.i)} onMouseLeave={() => setHover(null)}
              />
            ))}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="font-extrabold text-[17px] text-[#181621] tabular-nums">{eur(active.monthly_budget)}</span>
            <span className="text-[10.5px] text-[#9A9591]">/ month</span>
          </div>
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          {active.channels.map((c, i) => (
            <div
              key={i}
              className="flex items-center gap-2 text-[12.5px] cursor-default rounded-md px-1.5 py-0.5 -mx-1.5"
              style={{ background: hover === i ? "#F3EFE7" : "transparent", opacity: hover === null || hover === i ? 1 : 0.45, transition: "opacity .18s, background .18s" }}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              data-testid={`donut-legend-${i}`}
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
              <span className="text-[#181621] truncate">{c.name}</span>
              <span className="ml-auto font-semibold text-[#54505B] tabular-nums">{Math.round(c.share_pct)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const KeyFiguresCard = ({ active, primary }) => {
  const organic = active.channels.find((c) => c.type === "organic" && c.expected_kpi);
  const rows = [
    primary && ["Primary channel", `${primary.label} · ${primary.share}%`],
    organic && ["Organic reach", `${compact(organic.expected_kpi.range_low)}–${compact(organic.expected_kpi.range_high)} ${organic.expected_kpi.metric}`],
    ["Channels", `${active.channels.length} active · ${active.do_not_do.length} held`],
  ].filter(Boolean);
  return (
    <div className="card-soft p-5 sm:p-6 flex flex-col" data-testid="key-figures-card">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[13px] font-semibold text-[#54505B]">Key figures</p>
        <span className="text-[11px] text-[#9A9591]">this plan</span>
      </div>
      <div className="mt-2 divide-y divide-[#F3EFE7]">
        {rows.map(([k, v]) => (
          <div key={k} className="py-3 flex items-baseline justify-between gap-3">
            <span className="text-[12.5px] text-[#9A9591]">{k}</span>
            <span className="text-[13.5px] font-bold text-[#181621] text-right tabular-nums">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const GeneratingCard = () => (
  <ProcessingLoader
    eyebrow="Creating your strategy"
    title="Creating your strategy…"
    description="Turning your business information into a clear and structured plan."
    testId="strategy-generating-state"
  />
);

const ErrorCard = ({ onRetry }) => (
  <div className="card-soft mt-6 p-10 sm:p-14 flex flex-col items-center text-center" data-testid="strategy-error-state">
    <div className="icon-tile !w-14 !h-14" style={{ background: "var(--amber-bg)" }}>
      <AlertTriangle size={26} strokeWidth={1.8} style={{ color: "var(--amber-fg)" }} />
    </div>
    <p className="mt-5 text-lg font-bold text-[#181621]">Something broke on our side. Try again.</p>
    <p className="text-[14px] text-[#54505B] mt-1">Your Brand Brain is safe. The engine just needs another run.</p>
    <CtaButton className="mt-7" onClick={onRetry} data-testid="strategy-retry-btn">
      <RefreshCw size={15} strokeWidth={1.8} />
      Try again
    </CtaButton>
  </div>
);

const TypePill = ({ type }) => {
  const t = TYPE_TAGS[type] || TYPE_TAGS.owned;
  return (
    <Tag color={t.color} className="!normal-case">
      <span className="w-1.5 h-1.5 rounded-full bg-current inline-block mr-1 opacity-70" />
      {t.label}
    </Tag>
  );
};

const AllocationTable = ({ scenario, businessName }) => (
  <div className="card-soft mt-4 overflow-hidden" data-testid="allocation-table">
    <div className="flex items-baseline justify-between px-6 pt-5 pb-3">
      <p className="text-[14.5px] font-bold text-[#181621]">Channel plan</p>
      <p className="text-[11.5px] text-[#9A9591] tabular-nums">{scenario.channels.length} active · {scenario.do_not_do.length} not recommended</p>
    </div>
    <div className="hidden sm:grid grid-cols-[1.5fr_7.5rem_5rem_1.1fr_5.5rem] gap-3 px-6 py-2.5 border-y border-[#F3EFE7] text-[10.5px] font-semibold text-[#9A9591]">
      <span>Channel</span>
      <span>Type</span>
      <span className="text-right">Share</span>
      <span className="text-right">Est. KPI *</span>
      <span className="text-right">Monthly</span>
    </div>
    {scenario.channels.map((c, i) => (
      <div key={i} className="px-6 py-4 border-b border-[#F3EFE7]" data-testid={`channel-row-${i}`}>
        <div className="grid grid-cols-2 sm:grid-cols-[1.5fr_7.5rem_5rem_1.1fr_5.5rem] gap-x-3 gap-y-1 items-start">
          <div className="min-w-0">
            <span className="flex items-center gap-2 font-bold text-[14px] text-[#181621]">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
              {c.name}
            </span>
            <p className="text-[12px] text-[#9A9591] mt-1 leading-snug sm:pr-4">{c.rationale}</p>
          </div>
          <span className="justify-self-end sm:justify-self-start"><TypePill type={c.type} /></span>
          <span className="hidden sm:block text-right">
            <span className="text-[13px] font-semibold text-[#181621] tabular-nums">{Math.round(c.share_pct)}%</span>
            <span className="block h-1 rounded-full bg-[#F3EFE7] mt-1.5 overflow-hidden">
              <span className="block h-full rounded-full" style={{ width: `${Math.min(100, c.share_pct)}%`, background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            </span>
          </span>
          <span className="hidden sm:block text-right" title={c.expected_kpi ? DISCLAIMER : undefined}>
            {c.expected_kpi ? (
              <>
                <span className="font-semibold text-[13px] text-[#181621] tabular-nums">{compact(c.expected_kpi.range_low)}–{compact(c.expected_kpi.range_high)}</span>
                <span className="block text-[11px] text-[#9A9591]">{c.expected_kpi.metric}</span>
              </>
            ) : (
              <span className="text-[13px] text-[#9A9591]" data-testid="kpi-empty">—</span>
            )}
          </span>
          <span className="hidden sm:block text-right font-bold text-[14px] text-[#181621] tabular-nums">{eur(c.monthly_amount)}</span>
        </div>
        <div className="sm:hidden flex items-center justify-between mt-2 text-[12.5px]">
          <span className="text-[#54505B] tabular-nums">{Math.round(c.share_pct)}% · {c.expected_kpi ? `${compact(c.expected_kpi.range_low)}–${compact(c.expected_kpi.range_high)} ${c.expected_kpi.metric}` : "—"}</span>
          <span className="font-bold text-[#181621] tabular-nums">{eur(c.monthly_amount)}</span>
        </div>
      </div>
    ))}
    {scenario.do_not_do.length > 0 && (
      <p className="px-6 pt-4 pb-1 text-[11px] font-semibold text-[#9A9591]" data-testid="not-recommended-subheader">
        Not recommended for {businessName || "you"} right now
      </p>
    )}
    {scenario.do_not_do.map((d, i) => (
      <div key={`skip-${i}`} className="px-6 py-3.5 border-b border-[#F3EFE7] last:border-0 bg-[#FCFBF9]" data-testid={`skip-row-${i}`}>
        <div className="flex items-center gap-3">
          <span className="font-bold text-[13.5px] text-[#9A9591] line-through">{d.channel}</span>
          <Tag color="neutral" className="!normal-case">Not recommended</Tag>
        </div>
        <p className="text-[12px] text-[#9A9591] mt-1 leading-snug">{d.reason}</p>
      </div>
    ))}
    <p className="px-6 py-3 text-[11px] text-[#9A9591] bg-[#FCFBF9]">* {DISCLAIMER}.</p>
  </div>
);

const BudgetCard = ({ scenarios, slider, setSlider, isCustom, onRecalculate, complete, active }) => (
  <div className="card-soft mt-4 p-6 sm:p-7" data-testid="budget-slider-zone">
    <div className="flex flex-wrap gap-6 items-start justify-between">
      <div>
        <p className="text-[13px] font-semibold text-[#54505B]">Monthly budget</p>
        <p className="mt-1.5">
          <span className="text-[32px] font-extrabold tracking-tight text-[#181621] tabular-nums" data-testid="slider-budget-value">{eur(slider)}</span>
          <span className="text-sm text-[#9A9591] ml-1.5">/ mo</span>
          {isCustom && <Tag color="amber" className="ml-3 align-middle !normal-case">Custom, recalculate</Tag>}
          {!complete && <Tag color="neutral" className="ml-3 align-middle animate-pulse !normal-case" data-testid="planning-more-scenarios-tag">Planning 3 more scenarios…</Tag>}
        </p>
        <CtaButton className="mt-4" onClick={onRecalculate} disabled={!complete} data-testid="strategy-recalculate-btn">
          <RefreshCw size={15} strokeWidth={1.8} />
          Recalculate for {eur(slider)}
        </CtaButton>
      </div>
      <div className="min-w-0">
        <p className="text-[12px] text-[#9A9591]">Scenarios: pick a budget to see its plan</p>
        <div className="flex gap-2.5 mt-2.5 flex-wrap">
          {scenarios.map((s) => {
            const current = active && s.monthly_budget === active.monthly_budget && !isCustom;
            return (
              <button
                key={s.monthly_budget}
                onClick={() => setSlider(s.monthly_budget)}
                className={`rounded-xl border px-4 py-3 text-left transition-colors ${current ? "border-[#181621] bg-[#FAF8F3]" : "border-[#EAE5DB] bg-white hover:border-[#CFC9BD]"}`}
                data-testid={`scenario-marker-${s.monthly_budget}`}
              >
                <span className="flex items-center gap-2">
                  <span className="font-extrabold text-[15px] text-[#181621] tabular-nums">{eur(s.monthly_budget)}</span>
                  {current && <span className="bg-[#181621] text-[#FAF8F3] rounded-full px-2 py-0.5 text-[9.5px] font-bold">Current</span>}
                </span>
                <span className="block text-[11px] text-[#9A9591] mt-0.5">{s.channels.length} channels</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
    <div className="relative mt-7">
      <input
        type="range"
        min="0"
        max="100"
        step="0.5"
        value={budgetToPos(slider)}
        onChange={(e) => setSlider(posToBudget(Number(e.target.value)))}
        disabled={!complete}
        className="budget-slider disabled:opacity-40"
        data-testid="strategy-budget-slider"
      />
      <p className="text-[11.5px] text-[#9A9591] mt-2">Slide to any budget, or pick a scenario above. Custom numbers recalculate the plan.</p>
    </div>
  </div>
);

export default function Strategy() {
  const [brain, setBrain] = useState(null);
  const [plan, setPlan] = useState(undefined);
  const [phase, setPhase] = useState("idle"); // idle | generating | error
  const [jobStatus, setJobStatus] = useState(null);
  const [slider, setSlider] = useState(1000);
  const [sharpenOpen, setSharpenOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem("agz_sharpen_dismissed") === "1");
  const pollRef = useRef(null);
  const lastBudgetRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const loadPlan = async (snapSlider) => {
    const { data } = await api.get("/strategy");
    if (data) {
      setPlan(data);
      if (snapSlider) setSlider(data.anchor_budget);
    }
    return data;
  };

  const startPolling = () => {
    stopPolling();
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 300) {
        stopPolling();
        setPhase("error");
        return;
      }
      try {
        const { data } = await api.get("/strategy/status");
        setJobStatus(data.status);
        if (data.status === "failed") {
          stopPolling();
          setPhase("error");
        } else if (data.status === "primary_ready") {
          const p = await loadPlan(phaseRef.current === "generating");
          if (p) setPhase("idle");
        } else if (data.status === "complete") {
          stopPolling();
          await loadPlan(true);
          setPhase("idle");
          toast("Plan ready.", { description: "Every euro has a job now." });
        }
      } catch {
        /* transient poll failure — keep polling */
      }
    }, 3000);
  };

  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const generate = async (budget) => {
    lastBudgetRef.current = budget || null;
    setPhase("generating");
    try {
      await api.post("/strategy/generate", budget ? { budget } : {});
      startPolling();
    } catch {
      setPhase("error");
    }
  };

  useEffect(() => {
    api.get("/brand-brain").then(({ data }) => setBrain(data)).catch(() => {});
    (async () => {
      try {
        const { data: job } = await api.get("/strategy/status");
        setJobStatus(job.status);
        const p = await api.get("/strategy").then((r) => r.data).catch(() => null);
        setPlan(p || null);
        if (p) setSlider(p.anchor_budget);
        if (job.status === "pending" || job.status === "primary_ready") {
          if (!p || !p.complete) setPhase("generating");
          startPolling();
        } else if (job.status === "failed" && !p) {
          setPhase("error");
        }
      } catch {
        setPlan(null);
      }
    })();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(() => {
    if (!plan) return null;
    return plan.scenarios.reduce((best, s) =>
      Math.abs(s.monthly_budget - slider) < Math.abs(best.monthly_budget - slider) ? s : best
    );
  }, [plan, slider]);

  const isCustom = active ? Math.abs(slider - active.monthly_budget) / active.monthly_budget > 0.12 : false;

  const projection = useMemo(() => {
    if (!active) return null;
    const withKpi = active.channels.filter((c) => c.expected_kpi);
    if (!withKpi.length) return null;
    const groups = {};
    withKpi.forEach((c) => { (groups[c.expected_kpi.metric] = groups[c.expected_kpi.metric] || []).push(c); });
    const metric = Object.keys(groups).sort((a, b) =>
      groups[b].length - groups[a].length ||
      groups[b].reduce((s, c) => s + c.monthly_amount, 0) - groups[a].reduce((s, c) => s + c.monthly_amount, 0)
    )[0];
    return {
      metric,
      low: groups[metric].reduce((s, c) => s + c.expected_kpi.range_low, 0),
      high: groups[metric].reduce((s, c) => s + c.expected_kpi.range_high, 0),
    };
  }, [active]);

  const primary = useMemo(() => {
    if (!active?.channels?.length) return null;
    const groups = {};
    active.channels.forEach((c) => {
      const key = c.name.split(/[\s·/-]+/)[0];
      groups[key] = groups[key] || { share: 0, names: [] };
      groups[key].share += c.share_pct;
      groups[key].names.push(c.name);
    });
    const [key, top] = Object.entries(groups).sort((a, b) => b[1].share - a[1].share)[0];
    return { label: top.names.length > 1 ? key : top.names[0], share: Math.round(top.share) };
  }, [active]);

  return (
    <div className="p-6 sm:p-10 max-w-6xl" data-testid="strategy-page">
      <p className="text-[12.5px] font-semibold text-[#9A9591]">CEO Dashboard · Strategy</p>
      <h1 className="mt-3 text-3xl sm:text-4xl leading-tight">
        <span className="font-serif-it">Your marketing </span>
        <span className="font-extrabold tracking-[-0.02em]">direction</span>
      </h1>

      {brain && <ContextBar brain={brain} live={jobStatus === "complete" || jobStatus === "primary_ready"} month={plan?.month || 1} />}

      {brain && (!brain.business_description || !brain.snapshot) && !bannerDismissed && (
        <div className="card-soft mt-4 p-5 flex flex-wrap items-center justify-between gap-4 border-l-4 border-l-[#E0603A]" data-testid="sharpen-banner">
          <div className="flex items-start gap-3">
            <Sparkles size={18} strokeWidth={1.8} className="text-[#E0603A] mt-0.5 shrink-0" />
            <div>
              <p className="font-bold text-[14px] text-[#181621]">Sharpen your plan: complete your business profile (2 min).</p>
              <p className="text-[12.5px] text-[#54505B] mt-0.5">Tell me what you actually do, in your own words, and every plan gets built on your real business instead of a generic category.</p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <CtaButton className="!py-2 !px-4 text-[13px]" onClick={() => setSharpenOpen(true)} data-testid="sharpen-open-btn">
              Complete profile
            </CtaButton>
            <button
              onClick={() => { localStorage.setItem("agz_sharpen_dismissed", "1"); setBannerDismissed(true); }}
              className="text-[#9A9591] hover:text-[#181621] transition-colors"
              aria-label="Dismiss"
              data-testid="sharpen-dismiss-btn"
            >
              <X size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}

      {brain && (
        <ProfileSharpen
          open={sharpenOpen}
          onOpenChange={setSharpenOpen}
          brain={brain}
          onDone={() => api.get("/brand-brain").then(({ data }) => setBrain(data)).catch(() => {})}
          onRegenerate={() => generate()}
        />
      )}

      {plan === undefined && phase === "idle" && (
        <div className="card-soft mt-6 p-10 animate-pulse h-40" data-testid="strategy-loading" />
      )}

      {phase === "generating" && <GeneratingCard />}

      {phase === "error" && (
        <ErrorCard onRetry={() => generate(lastBudgetRef.current || undefined)} />
      )}

      {phase === "idle" && plan === null && (
        <div className="card-soft mt-6 p-10 sm:p-14 flex flex-col items-center text-center" data-testid="strategy-empty-state">
          <div className="icon-tile !w-14 !h-14" style={{ background: "var(--neutral-bg)" }}>
            <Compass size={26} strokeWidth={1.8} style={{ color: "var(--neutral-fg)" }} />
          </div>
          <p className="mt-5 text-lg font-bold text-[#181621]">No plan yet.</p>
          <p className="text-[14px] text-[#54505B] mt-1">Give me a budget and 60 seconds.</p>
          <CtaButton className="mt-7" onClick={() => generate()} data-testid="strategy-generate-plan-btn">
            Generate my plan
          </CtaButton>
        </div>
      )}

      {phase === "idle" && plan && active && (
        <>
          <div className="grid lg:grid-cols-[1fr_1.15fr_1fr] gap-4 mt-4">
            <ProjectedCard active={active} projection={projection} />
            <DonutCard active={active} />
            <KeyFiguresCard active={active} primary={primary} />
          </div>

          <BudgetCard
            scenarios={plan.scenarios}
            slider={slider}
            setSlider={setSlider}
            isCustom={isCustom}
            onRecalculate={() => generate(Math.max(100, Math.min(50000, slider)))}
            complete={plan.complete !== false}
            active={active}
          />

          <div className="card-soft mt-4 p-6 sm:p-7" data-testid="headline-insight-card">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-[13px] font-semibold text-[#54505B]">The direction</p>
              <Tag color="green" className="!normal-case">Estimate</Tag>
            </div>
            <p className="mt-3 text-[16px] sm:text-[17px] font-bold leading-relaxed text-[#181621] max-w-3xl" data-testid="headline-insight-text">
              {plan.headline_insight}
            </p>
            <p className="text-[11px] text-[#9A9591] mt-3">{DISCLAIMER}.</p>
          </div>

          <AllocationTable scenario={active} businessName={brain?.business_name} />

          {active.offline_actions?.length > 0 && (
            <div className="card-soft mt-4 p-6 sm:p-7" data-testid="offline-actions-card">
              <p className="text-[13px] font-semibold text-[#54505B]">Offline, because you're local</p>
              <ul className="mt-4 space-y-2.5">
                {active.offline_actions.map((a, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-[#181621]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E0603A] mt-1.5 shrink-0" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
